import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, MessageCircle, FileDown, Star, Unlock, Save, Pencil, Trash2,
  CalendarClock, MapPin, Ban, HelpCircle, ExternalLink, AlertTriangle,
  Link2, Copy, Sparkles,
  Music2, ListChecks, ShieldCheck, Gift,
} from "lucide-react";
import { api, API, getAdminToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_META, EXECUTION_LABELS, SERVICES_META } from "@/lib/constants";
import { formatDatePt, formatDateShort } from "@/lib/format";
import { toast } from "sonner";
import { InfoLine, SectionCard, ChipList, AuthBadge, FestaTimeline } from "./ClientDetailParts";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [toDelete, setToDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/events/${id}`);
    setEv(data);
    setNotes(data.admin_notes || "");
    if (data.status === "enviado" && !data.viewed_by_admin) {
      api.patch(`/admin/events/${id}`, { viewed_by_admin: true }).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (payload, msg) => {
    await api.patch(`/admin/events/${id}`, payload);
    if (msg) toast.success(msg);
    load();
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await patch({ admin_notes: notes }, "Observações internas salvas.");
    } finally {
      setSavingNotes(false);
    }
  };

  const reopen = async () => {
    await api.post(`/admin/events/${id}/reopen`);
    toast.success("Edição liberada para o casal.");
    load();
  };

  const downloadPdf = (docType = "completo") => {
    window.open(`${API}/admin/events/${id}/pdf?doc=${docType}&token=${getAdminToken()}`, "_blank");
  };

  const editRepertoire = async () => {
    try {
      if (ev.locked) {
        await api.post(`/admin/events/${id}/reopen`);
        toast.success("Repertório liberado para edição.");
        await load();
      }
      window.open(`/r/${ev.token}`, "_blank");
    } catch {
      toast.error("Não foi possível abrir para edição.");
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Evento excluído.");
      navigate("/admin/clientes");
    } catch {
      toast.error("Não foi possível excluir.");
      setDeleting(false);
    }
  };

  const whatsapp = () => {
    const digits = (ev.phone || "").replace(/\D/g, "");
    window.open(`https://wa.me/55${digits}`, "_blank");
  };

  if (!ev)
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>;

  const rec = ev.reception || {};
  const styles = [...(rec.styles || [])];
  if (rec.other_style) styles.push(rec.other_style);
  const moments = [...(ev.moments || [])].sort((a, b) => a.order - b.order);
  const svc = ev.services || { cerimonia: true };
  const banda = ev.banda || {};
  const astro = ev.astrolive || {};
  const festa = ev.festa || {};
  const prods = ev.astrolive_products || {};
  const shareLink = `${window.location.origin}/r/${ev.token}`;

  const alerts = [];
  if (moments.some((m) => m.surprise?.is)) alerts.push({ tone: "star", text: "⭐ MÚSICA SURPRESA" });
  if ((astro.musicas_nao_desejadas || banda.musicas_nao_desejadas || "").trim()) alerts.push({ tone: "red", text: "🔴 NÃO TOCAR — ver restrições" });
  if (svc.astrolive && astro.bazuca_autorizada === "nao") alerts.push({ tone: "orange", text: "🟠 BAZUCA NÃO AUTORIZADA" });
  if (svc.astrolive && astro.areia_autorizada === "nao") alerts.push({ tone: "orange", text: "🟠 AREIA (LED) NÃO AUTORIZADA" });
  if (svc.astrolive && astro.perfil_evento !== "Gospel" && astro.bananinha_autorizada === "nao") alerts.push({ tone: "orange", text: "🟠 BEBIDA (BANANINHA) NÃO AUTORIZADA" });
  if (festa.outra_atracao) alerts.push({ tone: "blue", text: "🔵 OUTRA ATRAÇÃO NO EVENTO" });
  if (svc.banda && festa.show_banda_undefined) alerts.push({ tone: "yellow", text: "🟡 SHOW DA BANDA — HORÁRIO NÃO DEFINIDO" });
  if (svc.astrolive && festa.show_astrolive_undefined) alerts.push({ tone: "yellow", text: "🟡 SHOW ASTROLIVE — HORÁRIO NÃO DEFINIDO" });

  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Link copiado!"); };

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-muted)] hover:text-[color:var(--brand-graphite)]">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">{ev.couple_name}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-[color:var(--brand-muted)]">
            <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> {formatDatePt(ev.date)}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ev.local}</span>
            <StatusBadge status={ev.status} />
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.keys(SERVICES_META).filter((k) => svc[k]).map((k) => (
              <span key={k} className="rounded-full bg-[color:var(--brand-marsala-soft)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--brand-marsala)]">{SERVICES_META[k].label}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="detail-edit-repertoire" onClick={editRepertoire} className="h-10 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]"><Pencil className="mr-2 h-4 w-4" /> Editar repertório</Button>
          <Button data-testid="detail-whatsapp" variant="outline" onClick={whatsapp} className="h-10"><MessageCircle className="mr-2 h-4 w-4 text-[color:var(--brand-success)]" /> WhatsApp</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="detail-pdf" variant="outline" className="h-10"><FileDown className="mr-2 h-4 w-4" /> Documentos</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Por serviço</DropdownMenuLabel>
              <DropdownMenuItem data-testid="pdf-completo" onClick={() => downloadPdf("completo")}>Evento completo</DropdownMenuItem>
              {svc.cerimonia && <DropdownMenuItem data-testid="pdf-cerimonia" onClick={() => downloadPdf("cerimonia")}>Somente Cerimônia</DropdownMenuItem>}
              {svc.banda && <DropdownMenuItem data-testid="pdf-banda" onClick={() => downloadPdf("banda")}>Somente Banda / Receptivo</DropdownMenuItem>}
              {svc.astrolive && <DropdownMenuItem data-testid="pdf-astrolive" onClick={() => downloadPdf("astrolive")}>Somente AstroLive</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Documentos especiais</DropdownMenuLabel>
              <DropdownMenuItem data-testid="pdf-musicos" onClick={() => downloadPdf("musicos")}>Para os Músicos (repertório)</DropdownMenuItem>
              <DropdownMenuItem data-testid="pdf-empresa" onClick={() => downloadPdf("empresa")}>Empresa + Cliente (completo)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="detail-delete" variant="outline" onClick={() => setToDelete(true)} className="h-10 border-[color:var(--brand-danger)] text-[color:var(--brand-danger)] hover:bg-[#FDECEA]"><Trash2 className="mr-2 h-4 w-4" /> Excluir</Button>
        </div>
      </div>

      <AlertDialog open={toDelete} onOpenChange={setToDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este evento?</AlertDialogTitle>
            <AlertDialogDescription>
              "{ev.couple_name}" e todo o repertório enviado serão removidos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="detail-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting} className="bg-[color:var(--brand-danger)] text-white hover:bg-[color:var(--brand-danger)]/90">
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* shareable client link */}
      <div className="mt-4 flex flex-col gap-2 rounded-xl border border-[color:var(--brand-border)] bg-white p-3 sm:flex-row sm:items-center">
        <span className="flex items-center gap-1.5 text-xs font-medium text-[color:var(--brand-muted)]"><Link2 className="h-4 w-4" /> Link do cliente:</span>
        <code data-testid="detail-share-link" className="flex-1 truncate rounded bg-[color:var(--brand-paper)] px-2 py-1 text-xs text-[color:var(--brand-graphite)]">{shareLink}</code>
        <div className="flex gap-2">
          <Button data-testid="detail-copy-link" size="sm" variant="outline" onClick={copyLink} className="h-8"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copiar</Button>
          <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/55${(ev.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent("Olá! Segue o link para montar o repertório do seu evento na LC Produção: " + shareLink)}`, "_blank")} className="h-8"><MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Enviar</Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {alerts.map((a, i) => (
            <span key={a.text} data-testid="detail-alert" className={`rounded-lg px-2.5 py-1 text-xs font-bold ${a.tone === "red" ? "bg-[#FDECEA] text-[color:var(--brand-danger)]" : a.tone === "orange" ? "bg-[#FBF0E4] text-[color:var(--brand-warning)]" : a.tone === "yellow" ? "bg-[#FBF7E4] text-[color:var(--brand-warning)]" : a.tone === "blue" ? "bg-[#E9F1FA] text-[color:var(--brand-info)]" : "bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]"}`}>{a.text}</span>
          ))}
        </div>
      )}

      {ev.reopen_requested && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-sm font-medium text-[color:var(--brand-marsala)]">
            <AlertTriangle className="h-4 w-4" /> {ev.couple_name} solicitaram reabertura do repertório.
          </p>
          <Button data-testid="detail-reopen" onClick={reopen} className="h-10 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]"><Unlock className="mr-2 h-4 w-4" /> Liberar edição</Button>
        </div>
      )}

      <Tabs defaultValue="info" className="mt-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="info" data-testid="detail-tab-info">Visão geral</TabsTrigger>
          {svc.cerimonia && <TabsTrigger value="repertorio" data-testid="detail-tab-repertorio">Cerimônia</TabsTrigger>}
          {svc.banda && <TabsTrigger value="banda" data-testid="detail-tab-banda">Banda</TabsTrigger>}
          {svc.astrolive && <TabsTrigger value="astrolive" data-testid="detail-tab-astrolive">AstroLive</TabsTrigger>}
          {(svc.banda || svc.astrolive) && <TabsTrigger value="festa" data-testid="detail-tab-festa">Festa</TabsTrigger>}
          <TabsTrigger value="notas" data-testid="detail-tab-notas">Notas internas</TabsTrigger>
          <TabsTrigger value="historico" data-testid="detail-tab-historico">Histórico</TabsTrigger>
          <TabsTrigger value="status" data-testid="detail-tab-status">Status e prazos</TabsTrigger>
        </TabsList>

        {/* INFO */}
        <TabsContent value="info" className="mt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ["Casal", ev.couple_name],
              ["Data", formatDatePt(ev.date)],
              ["Local", ev.local],
              ["WhatsApp", ev.phone],
              ["Início do preenchimento", formatDateShort(ev.created_at)],
              ["Última alteração", formatDateShort(ev.updated_at)],
              ["Enviado em", ev.sent_at ? formatDateShort(ev.sent_at) : "—"],
              ["Progresso", `${ev.progress?.percent || 0}% (${ev.progress?.done}/${ev.progress?.total})`],
              ["Formação", ev.formation?.type || "—"],
              ["Instrumentos", (ev.formation?.instruments || []).length ? ev.formation.instruments.join(", ") : "—"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-[color:var(--brand-border)] bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-[color:var(--brand-muted)]">{k}</p>
                <p className="mt-1 text-sm font-medium text-[color:var(--brand-graphite)]">{v}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* REPERTÓRIO */}
        <TabsContent value="repertorio" className="mt-4 space-y-4">
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">Recepção pré-cerimonial</h3>
            <p className="mt-2 text-sm text-[color:var(--brand-graphite)]">{styles.length ? styles.join("  •  ") : "Sem estilos"}</p>
            {rec.references && <p className="mt-1 text-sm text-[color:var(--brand-muted)]"><span className="font-medium text-[color:var(--brand-graphite)]">Referências:</span> {rec.references}</p>}
          </div>

          {moments.map((m, i) => (
            <div key={m.id} className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-4">
              <div className="flex items-center justify-between">
                <h4 className="font-serif text-base font-semibold text-[color:var(--brand-graphite)]">{i + 1}. {m.name}</h4>
                {m.surprise?.is && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-[color:var(--brand-marsala)] px-2 py-1 text-[10px] font-bold text-white">
                    <Star className="h-3 w-3 fill-white" /> SURPRESA — NÃO REVELAR{m.surprise.for_whom ? ` (${m.surprise.for_whom})` : ""}
                  </span>
                )}
              </div>
              {m.not_applicable ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-muted)]"><Ban className="h-4 w-4" /> Não se aplica</p>
              ) : m.undecided ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-warning)]"><HelpCircle className="h-4 w-4" /> Ainda não decidido</p>
              ) : (
                <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                  <p className="text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">Música:</span> {(m.music || {}).track_name || "—"}</p>
                  <p className="text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">Artista:</span> {(m.music || {}).artist_name || "—"}</p>
                  <p className="text-[color:var(--brand-graphite)]"><span className="text-[color:var(--brand-muted)]">Execução:</span> {m.execution_type ? EXECUTION_LABELS[m.execution_type] : "—"}{m.execution_detail ? ` — ${m.execution_detail}` : ""}</p>
                  {(m.manual_link || (m.music || {}).link) && (
                    <a href={m.manual_link || m.music.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[color:var(--brand-marsala)] hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" /> Abrir referência
                    </a>
                  )}
                  {m.observations && <p className="sm:col-span-2 text-[color:var(--brand-muted)]"><span className="font-medium text-[color:var(--brand-graphite)]">Obs:</span> {m.observations}</p>}
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        {svc.banda && (
          <TabsContent value="banda" className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">Banda / Receptivo</h3>
              {banda.formato && <span className="rounded-full bg-[color:var(--brand-graphite)] px-2.5 py-0.5 text-xs font-medium text-white">{{ banda: "Apenas Banda", receptivo: "Apenas Receptivo", ambos: "Banda + Receptivo" }[banda.formato] || banda.formato}</span>}
              {banda.perfil && <span className="rounded-full bg-[color:var(--brand-marsala-soft)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--brand-marsala)]">{banda.perfil === "Outro" ? banda.perfil_outro : banda.perfil}</span>}
            </div>
            <SectionCard title="Estilos desejados" icon={ListChecks}>
              <ChipList items={[...(banda.estilos || []), banda.estilos_outros]} />
            </SectionCard>
            {banda.musicas_desejadas && (
              <SectionCard title="Músicas / artistas desejados" icon={Music2}>
                <p className="whitespace-pre-wrap text-sm text-[color:var(--brand-graphite)]">{banda.musicas_desejadas}</p>
              </SectionCard>
            )}
            {(banda.musicas_nao_desejadas || "").trim() && (
              <SectionCard title="Não tocar — restrições" icon={Ban} tone="danger">
                <p className="whitespace-pre-wrap text-sm font-medium text-[color:var(--brand-danger)]">{banda.musicas_nao_desejadas}</p>
              </SectionCard>
            )}
          </TabsContent>
        )}

        {svc.astrolive && (
          <TabsContent value="astrolive" className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">AstroLive</h3>
              {astro.perfil_evento && <span className="rounded-full bg-[color:var(--brand-marsala-soft)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--brand-marsala)]">{astro.perfil_evento}</span>}
              {astro.interacao && <span className="rounded-full bg-[color:var(--brand-champagne-soft)] px-2.5 py-0.5 text-xs font-medium text-[color:var(--brand-graphite)]">{astro.interacao}</span>}
            </div>

            <SectionCard title="Intuito do show" icon={Sparkles}>
              <p className="text-sm text-[color:var(--brand-graphite)]">{(astro.intuito === "Outro." ? astro.intuito_outro : astro.intuito) || "—"}</p>
            </SectionCard>

            {astro.musicas_desejadas && (
              <SectionCard title="Músicas desejadas" icon={Music2}>
                <p className="whitespace-pre-wrap text-sm text-[color:var(--brand-graphite)]">{astro.musicas_desejadas}</p>
              </SectionCard>
            )}

            {astro.playlist_link && (
              <SectionCard title="Playlist de inspiração" icon={ExternalLink}>
                <a href={astro.playlist_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 break-all text-sm font-medium text-[color:var(--brand-marsala)] hover:underline">
                  <ExternalLink className="h-4 w-4 shrink-0" /> {astro.playlist_link}
                </a>
              </SectionCard>
            )}

            {(astro.musicas_nao_desejadas || "").trim() && (
              <SectionCard title="Não tocar — restrições" icon={Ban} tone="danger">
                <p className="whitespace-pre-wrap text-sm font-medium text-[color:var(--brand-danger)]">{astro.musicas_nao_desejadas}</p>
              </SectionCard>
            )}

            <SectionCard title="Autorizações" icon={ShieldCheck}>
              <div className="space-y-2">
                {astro.perfil_evento !== "Gospel" && <AuthBadge label="Bebida alcoólica (bananinha)" value={astro.bananinha_autorizada} />}
                <AuthBadge label="Areia nos tambores de LED" value={astro.areia_autorizada} />
                <AuthBadge label="Bazuca de CO₂" value={astro.bazuca_autorizada} />
              </div>
            </SectionCard>

            {astro.brinde === "sim" && (
              <SectionCard title="Brindes contratados" icon={Gift}>
                <div className="flex flex-wrap gap-1.5">
                  {[["Óculos", astro.brinde_oculos], ["Bastões de LED", astro.brinde_bastoes], ["Boné", astro.brinde_bone], [astro.brinde_outro, astro.brinde_outro_qtd]]
                    .filter(([lbl, qtd]) => lbl && qtd)
                    .map(([lbl, qtd], i) => (
                      <span key={lbl} className="rounded-full bg-[color:var(--brand-champagne-soft)] px-2.5 py-1 text-xs font-medium text-[color:var(--brand-graphite)]">{lbl}: {qtd}</span>
                    ))}
                  {!(astro.brinde_oculos || astro.brinde_bastoes || astro.brinde_bone || (astro.brinde_outro && astro.brinde_outro_qtd)) && (
                    <span className="text-sm text-[color:var(--brand-muted)]">Contém brindes (quantidades não informadas).</span>
                  )}
                </div>
              </SectionCard>
            )}
            {astro.brinde === "nao" && (
              <SectionCard title="Brindes contratados" icon={Gift}>
                <p className="text-sm text-[color:var(--brand-muted)]">Sem brindes neste pacote.</p>
              </SectionCard>
            )}

            {astro.observacoes && (
              <SectionCard title="Observações" icon={Music2}>
                <p className="whitespace-pre-wrap text-sm text-[color:var(--brand-graphite)]">{astro.observacoes}</p>
              </SectionCard>
            )}
          </TabsContent>
        )}

        {(svc.banda || svc.astrolive) && (
          <TabsContent value="festa" className="mt-4 space-y-4">
            <FestaTimeline festa={festa} />
            <div className="space-y-1 rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
              <h3 className="mb-2 font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">Dados da festa</h3>
              <InfoLine label="Início da festa" value={festa.inicio} />
              {svc.banda && <InfoLine label="Horário do show — Banda / Receptivo" value={festa.show_banda_undefined ? "Ainda não definido" : ((festa.show_banda || festa.show) ? `${festa.show_banda || festa.show}${festa.show_banda_fim ? ` – ${festa.show_banda_fim}` : ""}` : "")} danger={festa.show_banda_undefined} />}
              {svc.astrolive && <InfoLine label="Horário do show — AstroLive" value={festa.show_astrolive_undefined ? "Ainda não definido" : (festa.show_astrolive ? `${festa.show_astrolive}${festa.show_astrolive_fim ? ` – ${festa.show_astrolive_fim}` : ""}` : "")} danger={festa.show_astrolive_undefined} />}
              {!svc.banda && !svc.astrolive && <InfoLine label="Horário do show" value={festa.show_undefined ? "Ainda não definido" : festa.show} danger={festa.show_undefined} />}
              <InfoLine label="Encerramento" value={festa.encerramento} />
              {festa.outra_atracao && <InfoLine label="Outra atração" value={(festa.outra_atracao_nome || "Sim") + (festa.outra_atracao_horario ? ` (${festa.outra_atracao_horario}${festa.outra_atracao_fim ? ` – ${festa.outra_atracao_fim}` : ""})` : "")} />}
              <InfoLine label="Sonorização LC" value={festa.sonorizacao === "sim" ? "Sim" : festa.sonorizacao === "nao" ? "Não" : festa.sonorizacao === "nao_sei" ? "A confirmar" : ""} />
              <InfoLine label="Palco no local" value={festa.possui_palco} />
              <InfoLine label="Obs. do palco" value={festa.palco_obs} />
            </div>
          </TabsContent>
        )}

        {/* NOTAS */}
        <TabsContent value="notas" className="mt-4">
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            <Label className="text-sm">Observações internas <span className="text-[color:var(--brand-muted)]">(nunca visível aos noivos)</span></Label>
            <Textarea data-testid="detail-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Violinista deverá iniciar somente quando a noiva aparecer." className="mt-2 min-h-[140px]" />
            <Button data-testid="detail-notes-save" onClick={saveNotes} disabled={savingNotes} className="mt-3 h-10 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
              {savingNotes ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salvar observações
            </Button>
          </div>
        </TabsContent>

        {/* HISTÓRICO */}
        <TabsContent value="historico" className="mt-4">
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            {(ev.history || []).length === 0 ? (
              <p className="text-sm text-[color:var(--brand-muted)]">Sem histórico ainda.</p>
            ) : (
              <ol className="space-y-4">
                {ev.history.map((h) => (
                  <li key={h.id} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[color:var(--brand-champagne)]" />
                    <div>
                      <p className="text-sm text-[color:var(--brand-graphite)]">{h.action}</p>
                      <p className="text-xs text-[color:var(--brand-muted)]">{formatDateShort(h.timestamp)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </TabsContent>

        {/* STATUS */}
        <TabsContent value="status" className="mt-4 space-y-4">
          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            <Label className="text-sm">Status do repertório</Label>
            <Select value={ev.status} onValueChange={(v) => patch({ status: v }, "Status atualizado.")}>
              <SelectTrigger data-testid="detail-status-select" className="mt-2 h-11 sm:w-72"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            <Label className="text-sm">Prazo para preenchimento</Label>
            <div className="mt-2 flex gap-2">
              <Input data-testid="detail-deadline-input" type="date" defaultValue={ev.deadline || ""} onBlur={(e) => patch({ deadline: e.target.value }, "Prazo definido.")} className="h-11 sm:w-56" />
            </div>
            <p className="mt-2 text-xs text-[color:var(--brand-muted)]">O casal verá: "Seu repertório deverá ser enviado até {ev.deadline ? formatDatePt(ev.deadline) : "—"}".</p>
          </div>

          <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-5">
            <Label className="text-sm">Edição do casal</Label>
            <p className="mt-1 text-sm text-[color:var(--brand-muted)]">{ev.locked ? "Repertório bloqueado (enviado)." : "Repertório aberto para edição."}</p>
            {ev.locked && (
              <Button data-testid="detail-reopen-status" onClick={reopen} className="mt-3 h-10 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]"><Unlock className="mr-2 h-4 w-4" /> Liberar edição</Button>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
